/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { Link, usePathname, useTranslations } from "@i18n/next";
import { HallowABFlag, useMixpanel } from "@packages/ab";
import { HallowAnalyticsEvent, useAnalytics } from "@packages/analytics";
import { URL_ONBOARDING, URL_SELF } from "@packages/sdk";
import { Button, useDynamicViewport } from "@packages/ui/emotion";
import { useEffect, useMemo, useState } from "react";

import { useIsSSTest } from "../../app/[locale]/index/(ss-ab)/useIsSSTest";
import { LINKS } from "../../constants";
import { useABLoadingWithTimeout } from "../../hooks/useABLoadingWithTimeout";
import type { MenuBarProps } from "./MenuBar";

type TryHallowForFreeProps = {
  abTests: MenuBarProps["abTests"];
};

const disabledOverride: Interpolation = {
  opacity: 1,
};

const Internal = ({ abTests }: TryHallowForFreeProps) => {
  // note the different min-width, copied from MenuBar.tsx
  const isDesktop = useDynamicViewport({ minimumWidth: 1024 });
  const analytics = useAnalytics();
  const pathname = usePathname();
  const t = useTranslations();
  const { flags } = useMixpanel();
  const homepageCtaTest =
    flags?.[HallowABFlag.WebHomepageCTADestination]?.isTargeted ?? false;

  const isSSTest = useIsSSTest({
    variants: ["control", "jesus", "content"],
  });

  const [isClient, setIsClient] = useState<boolean>(false);

  const fallbackUrl = useMemo(() => {
    if (isDesktop) {
      return `${URL_ONBOARDING}/?utm_source=website&utm_medium=website&utm_campaign=Website-NavLink&subscribeRedirect=${encodeURIComponent(`${URL_SELF}${pathname}`)}`;
    }

    return LINKS.tryHallowForFree.props.href;
  }, [isDesktop]);

  const url = useMemo(() => {
    if (pathname !== "/" || !abTests?.[HallowABFlag.WebHomepageCTADestination])
      return fallbackUrl;

    if (homepageCtaTest)
      return abTests[HallowABFlag.WebHomepageCTADestination].treatment;

    return abTests[HallowABFlag.WebHomepageCTADestination].control;
  }, [pathname, fallbackUrl, homepageCtaTest]);

  useEffect(() => {
    setIsClient(typeof window !== "undefined");
  }, []);

  if (!isClient)
    return (
      <Button css={disabledOverride} disabled>
        {t(LINKS.tryHallowForFree.message)}
      </Button>
    );

  return (
    <Button
      asChild
      onClickCapture={() =>
        analytics.track({
          event: HallowAnalyticsEvent.TappedTryHallowForFree,
          properties: {
            button_location: "menu_bar",
          },
        })
      }
      size={isDesktop ? "m" : "l"}
      isFullWidth={!isDesktop}
    >
      <Link
        {...LINKS.tryHallowForFree.props}
        href={isSSTest ? URL_ONBOARDING : url}
      >
        {t(LINKS.tryHallowForFree.message)}
      </Link>
    </Button>
  );
};

export const TryHallowForFree = ({ abTests }: TryHallowForFreeProps) => {
  const { isLoading } = useABLoadingWithTimeout();
  const t = useTranslations();
  if (isLoading)
    return (
      <Button css={disabledOverride} disabled>
        {t(LINKS.tryHallowForFree.message)}
      </Button>
    );
  return <Internal abTests={abTests} />;
};
